import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, OnDestroy } from '@angular/core';
import { fromEvent, merge, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

import { SecurityEventsService } from './security-events.service';
import { SecurityStateService } from './security-state.service';

@Injectable({
  providedIn: 'root',
})
export class DisableMediaSavingService implements OnDestroy {
  private initialized = false;
  private suspended = true;

  private readonly allowedDomains = [environment.fullDomain];

  private readonly activeClass = 'media-saving-disabled';
  private readonly protectedClass = 'media-saving-protected';

  private readonly destroy$ = new Subject<void>();

  private styleElement: HTMLStyleElement | null = null;
  private mutationObserver: MutationObserver | null = null;

  constructor(
    @Inject(DOCUMENT)
    private readonly document: Document,
    private readonly securityStateService: SecurityStateService,
    private readonly securityEventsService: SecurityEventsService,
  ) {}

  async initialize(): Promise<void> {
    if (this.initialized) {
      return;
    }

    const isEnabled = await this.securityStateService.isEnabled('open-contextmenu');

    if (!isEnabled) {
      return;
    }

    this.initialized = true;

    this.installStyles();
    this.scanImages();
    this.observeImages();
    this.listenForEvents();
  }

  suspend(): void {
    this.suspended = true;

    this.document.documentElement.classList.remove(this.activeClass);
  }

  resume(): void {
    this.suspended = false;

    this.scanImages();

    this.document.documentElement.classList.add(this.activeClass);
  }

  get isSuspended(): boolean {
    return this.suspended;
  }

  private listenForEvents(): void {
    merge(fromEvent(this.document, 'contextmenu'), fromEvent(this.document, 'dragstart'))
      .pipe(
        filter(() => !this.suspended),
        filter((event) => this.isProtectedEvent(event)),
        takeUntil(this.destroy$),
      )
      .subscribe((event) => {
        this.sendEvent(event);
        event.preventDefault();
      });
  }

  private installStyles(): void {
    if (this.styleElement) {
      return;
    }

    const style = this.document.createElement('style');

    style.setAttribute('data-disable-media-saving', 'true');

    style.textContent = `
      html.${this.activeClass} img.${this.protectedClass} {
        -webkit-touch-callout: none !important;
        -webkit-user-select: none !important;
        user-select: none !important;
        -webkit-user-drag: none !important;
      }
    `;

    this.document.head.appendChild(style);

    this.styleElement = style;
  }

  private scanImages(): void {
    const images = this.document.querySelectorAll<HTMLImageElement>('img');

    images.forEach((image) => {
      this.updateImage(image);
    });
  }

  private observeImages(): void {
    if (this.mutationObserver) {
      return;
    }

    this.mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'attributes') {
          if (mutation.target instanceof HTMLImageElement) {
            this.updateImage(mutation.target);
          }

          continue;
        }

        for (const node of Array.from(mutation.addedNodes)) {
          this.processAddedNode(node);
        }
      }
    });

    this.mutationObserver.observe(this.document.documentElement, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['src', 'srcset'],
    });
  }

  private processAddedNode(node: Node): void {
    if (!(node instanceof Element)) {
      return;
    }

    if (node instanceof HTMLImageElement) {
      this.updateImage(node);
    }

    const images = node.querySelectorAll<HTMLImageElement>('img');

    images.forEach((image) => {
      this.updateImage(image);
    });
  }

  private updateImage(image: HTMLImageElement): void {
    image.classList.toggle(this.protectedClass, this.isProtectedImage(image));
  }

  private isProtectedEvent(event: Event): boolean {
    const image = this.getImageFromEvent(event);

    if (!image) {
      return false;
    }

    return this.isProtectedImage(image);
  }

  private getImageFromEvent(event: Event): HTMLImageElement | null {
    const target = event.target;

    if (!(target instanceof Element)) {
      return null;
    }

    return target.closest('img');
  }

  private isProtectedImage(image: HTMLImageElement): boolean {
    const src = image.currentSrc || image.src;

    if (!src) {
      return false;
    }

    try {
      const imageUrl = new URL(src, this.document.baseURI);

      const isAllowedDomain = this.allowedDomains.some((domain) => imageUrl.host === domain);

      return !isAllowedDomain;
    } catch {
      return false;
    }
  }

  private sendEvent(event: Event): void {
    const image = this.getImageFromEvent(event);

    if (!image) {
      return;
    }

    const src = image.currentSrc || image.src;

    this.securityEventsService.sendEvent('open-contextmenu', {
      mediaSrc: src,
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    this.mutationObserver?.disconnect();
    this.mutationObserver = null;

    this.styleElement?.remove();
    this.styleElement = null;

    this.document.documentElement.classList.remove(this.activeClass);

    this.document.querySelectorAll(`.${this.protectedClass}`).forEach((element) => {
      element.classList.remove(this.protectedClass);
    });
  }
}
